import React, { useCallback, useContext, useMemo } from 'react'

import PlannedHotelCtaContent from '../Hotel/PlannedHotelCtaContent'
import { ItemCardVariant } from '../ItemCard'
import { PlannedItemPriceContent } from '../PlannedItemPriceContent'

import TextButton from 'components/Luxkit/Button/TextButton'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import Modal from 'components/Luxkit/Modal/Modal'
import TextLink from 'components/Luxkit/TextLink'
import SearchRentalAmenities from 'components/OfferList/OfferListTiles/SearchOfferTiles/SearchVillaTile/SearchVillaAmenities'
import VillasPageAmenitiesModalContent from 'components/OfferPage/Villas/VillasPageAmenitiesModalContent'
import Group from 'components/utils/Group'
import GeoContext from 'contexts/geoContext'
import ModalContext from 'contexts/ModalContext'
import { nonNullable, sortBy, take } from 'lib/array/arrayUtils'
import { getMaxOccupancies } from 'lib/offer/occupancyUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import BookmarkLocation from 'tripPlanner/components/Bookmark/BookmarkCardNew/BookmarkLocation'
import SaveTripItemButton from 'tripPlanner/components/Buttons/SaveTripItemButton'
import LEOfferPriceDetails from 'tripPlanner/components/ItemOfferPrice/LEOfferPriceDetails'
import SavedOfferCard from 'tripPlanner/components/SavedOffers/SavedOfferCard'
import { VIEW_OFFER_COPY } from 'tripPlanner/config'
import TripContext from 'tripPlanner/contexts/TripContext'
import useAccommodationItemOccupancyDescription from 'tripPlanner/hooks/occupancy/useAccommodationItemOccupancyDescription'
import useAccommodationOfferAvailable from 'tripPlanner/hooks/useAccommodationOfferAvailable'
import { HomeAndVillasItem, LETripItem } from 'tripPlanner/types/tripItem'
import {
  formatShortDateRange,
  getOfferImage,
  isLeBookmark,
} from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'
import { getAccommodationPriceAndLabel } from 'tripPlanner/utils/hotel'
import { findHotelPackage } from 'tripPlanner/utils/offer'

interface Props {
  offer: App.VillaOffer
  item: HomeAndVillasItem & LETripItem
  onClickCard: (offerLink: string) => void
  onClickTripName?: () => void
  variant: ItemCardVariant
}

function HomeAndVillasBookmarkItemCard({
  offer,
  item,
  onClickCard,
  onClickTripName,
  variant,
}: Props) {
  const tripContext = useContext(TripContext)
  const viewAsTemplate = tripContext?.viewAsTemplate

  const { isTaxExclusiveRegion } = useContext(GeoContext)

  const { imgId, imgSrc } = getOfferImage(offer)

  const isOfferAvailable = useAccommodationOfferAvailable(
    offer,
    item.savedItemData,
    item.code,
  )

  const timeDescription = formatShortDateRange(item)
  const { occupanciesText, numberOfRoomsText } =
    useAccommodationItemOccupancyDescription(item)
  const timeAndOccupancyText = nonNullable([timeDescription, occupanciesText])
    .filter((text) => text.length > 0)
    .join(', ')

  const placeId = 'id' in item.place ? item.place.id : undefined
  const maxGuests = useMemo(() => {
    const allCapacities = offer.packages.flatMap((p) => p.roomRate.capacities)
    const max = getMaxOccupancies(
      allCapacities,
      { adults: 0 },
      {
        maxChildAge: offer.property.maxChildAge,
        maxInfantAge: offer.property.maxInfantAge,
      },
    )
    return `Up to ${pluralizeToString('guest', max.adults)}`
  }, [offer])

  const numberOfBedrooms = pluralizeToString('bedroom', offer.property.bedrooms)
  const numberOfBathrooms = pluralizeToString(
    'bathroom',
    offer.property.bathrooms,
  )
  const propertyDescription = useMemo(() => {
    return nonNullable([
      'Entire Villa',
      maxGuests,
      numberOfBedrooms,
      numberOfBathrooms,
    ])
      .filter((text) => text.length > 0)
      .join(' · ')
  }, [maxGuests, numberOfBathrooms, numberOfBedrooms])

  const offerLink: string = useMemo(() => {
    return (
      (isLeBookmark(item)
        ? buildProductLink(item, !viewAsTemplate, { offer })
        : '') ?? ''
    )
  }, [item, offer, viewAsTemplate])

  const { discountPercent, currencyCode } = item.savedItemData

  const [price, priceDisplayType, priceLabel] = getAccommodationPriceAndLabel(
    offer,
    item.savedItemData,
    isTaxExclusiveRegion,
  )

  const priceAreaContent = useMemo(() => {
    if (viewAsTemplate) {
      return (
        <LEOfferPriceDetails
          offer={offer as App.Offer}
          package={findHotelPackage(offer.packages, item)}
        />
      )
    } else {
      return (
        <PlannedItemPriceContent
          discountPercent={discountPercent}
          price={price}
          priceLabel={priceLabel}
          currencyCode={currencyCode}
          isOfferAvailable={isOfferAvailable}
          priceDisplayType={priceDisplayType}
        />
      )
    }
  }, [
    currencyCode,
    discountPercent,
    isOfferAvailable,
    item,
    offer,
    price,
    priceDisplayType,
    priceLabel,
    viewAsTemplate,
  ])

  const ctaAreaContent = useMemo(() => {
    if (viewAsTemplate) {
      return (
        <Group direction="horizontal" gap={12}>
          <SaveTripItemButton item={item} />
          <TextButton kind="primary" nonInteractable>
            {VIEW_OFFER_COPY}
          </TextButton>
        </Group>
      )
    } else {
      return (
        <PlannedHotelCtaContent
          viewOffer={() => {
            onClickCard(offerLink)
          }}
          item={item}
          isOfferAvailable={isOfferAvailable}
          variant={variant}
        />
      )
    }
  }, [viewAsTemplate, item, isOfferAvailable, variant, onClickCard, offerLink])

  const locationDescription = useMemo(
    () => (
      <BookmarkLocation
        variant="single"
        startLocation={item.place.region}
        showIcon={false}
      />
    ),
    [item.place.region],
  )
  const rating = offer.property.rating
  const numberOfReviews = offer.property.reviewsTotal
  const imageOverlayContent = useMemo(() => {
    return <ProductTypeLabel productType={offer.productType} />
  }, [offer.productType])

  const amenities = useMemo(
    () =>
      sortBy(
        offer.packages[0]?.roomType?.amenities ?? [],
        (amenity) => amenity.name,
        'asc',
      ),
    [offer],
  )

  const showModal = useContext(ModalContext)
  const openAmenitiesModal = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault()
      showModal(
        <Modal
          title="Amenities"
          primaryActionTo={offerLink}
          primaryActionText="View offer"
        >
          <VillasPageAmenitiesModalContent amenities={amenities} />
        </Modal>,
      )
    },
    [showModal, amenities, offerLink],
  )
  const amenitiesContent = useMemo(() => {
    return (
      amenities.length > 0 && (
        <Group direction="vertical" gap={4}>
          <SearchRentalAmenities amenities={take(amenities, 4)} />
          <TextLink
            weight="bold"
            underline
            size="medium"
            onClick={openAmenitiesModal}
          >
            View all {amenities.length} amenities
          </TextLink>
        </Group>
      )
    )
  }, [amenities, openAmenitiesModal])

  const onClickSavedOfferCard = useCallback(() => {
    onClickCard(offerLink)
  }, [offerLink, onClickCard])

  return (
    <SavedOfferCard
      item={item}
      name={item.place.name}
      propertyDescription={propertyDescription}
      imageOverlayContent={imageOverlayContent}
      imgSrc={imgSrc}
      imgId={imgId}
      locationDescription={locationDescription}
      descriptors={[timeAndOccupancyText, numberOfRoomsText]}
      placeId={placeId}
      priceAreaContent={priceAreaContent}
      ctaAreaContent={ctaAreaContent}
      onClick={onClickSavedOfferCard}
      offerLink={offerLink}
      isSoldOut={!isOfferAvailable}
      testId="saved-hotel-card"
      onClickTripName={onClickTripName}
      variant={variant}
      amenitiesContent={amenitiesContent}
      rating={rating}
      numberOfReviews={numberOfReviews}
    />
  )
}

export default HomeAndVillasBookmarkItemCard
